import { useContext, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { createOption } from 'utils/form';
import CatalogsContext, { CatalogsContextType } from 'context/catalogs/context';
import { getCatalogDetailsByCatalogCodes } from 'service/catalogsV2';

import { staticCatalogs } from 'utils/catalogs/staticCatalogs';
import {
  sort,
  sortTypeValue,
  sortLocale,
  sortBankers,
  sortArrayOfAlphaNum,
} from 'utils/array';
import {
  catalogKeys,
  catalogList,
  catalogSortByValue,
} from 'utils/catalogs/keys';

import type { CatalogListType } from 'context/catalogs/types';
import type { CatalogDetail } from 'commons/modules/service/catalogsV2';

const getCatalogList = (i: CatalogDetail, keyName: string) => {
  switch (i.catalogCode) {
    case catalogList.nationalities.keyName:
      return sortLocale(i.values, 'México', true);
    case catalogList.bankers.keyName:
      return sortBankers(i.values);
    case catalogList.profiles.keyName:
    case catalogList.profilesModal.keyName:
      return i.values.map((catalogData) =>
        createOption({
          label: catalogData.value01 || '',
          value: catalogData.code01 || '',
        }),
      );
    case catalogList.incomeLevels.keyName:
    case catalogList.incomeLevelsAdditional.keyName:
      return i.values.map((catalogData) =>
        createOption({
          label: catalogData.value01 || '',
          value: catalogData.code01 || '',
          description: catalogData.value02 || '',
        }),
      );
    case catalogList.marketValueType.keyName:
      return sortTypeValue(i.values);
    case catalogList.brokerageHouses.keyName:
      return i.values
        .map((catalogData) => ({
          ...createOption({
            label: catalogData.value01 || '',
            value: catalogData.code01 || '',
            description: catalogData.value02 || '',
          }),
          key03: catalogData.code02,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    case catalogList.movementClassifications.keyName:
      return i.values.map((catalogData) => ({
        ...createOption({
          label: catalogData.value01 || '',
          value: catalogData.code01 || '',
          description: catalogData.value02 || '',
        }),
        type: catalogData.code04 || '',
        key03: catalogData.code03,
        key04: catalogData.code04 || '',
      }));
    case catalogList.investmentAccountType.keyName:
      return sortArrayOfAlphaNum(
        i.values.map((catalogData) => ({
          label: catalogData.value01,
          value: catalogData.code01,
          key02: catalogData.code02,
        })),
        'key02',
      );
    case catalogList.daysInactive.keyName:
      return i.values.map((catalogData) => {
        return {
          ...createOption({
            label: catalogData.value01 || '',
            value: catalogData.value02 || '',
          }),
          code02: catalogData.code02,
          code03: catalogData.code03,
        };
      });
    default: {
      if (catalogSortByValue.includes(keyName)) {
        return sortArrayOfAlphaNum(sort(i.values, true), 'value');
      }

      return sort(i.values, true);
    }
  }
};

const buildCatalogs = (catalogData: CatalogDetail[]) => {
  const filteredCatalogs = { ...catalogList };

  for (const property in catalogList) {
    catalogData.forEach((i) => {
      if (
        i.catalogCode === catalogList[property as keyof CatalogListType].keyName
      ) {
        filteredCatalogs[property as keyof CatalogListType] = {
          ...catalogList[property as keyof CatalogListType],
          values: getCatalogList(i, i.catalogCode),
        };
      }
    });
  }
  return filteredCatalogs;
};

const useCatalogs = () => {
  const { onboardingCatalogs, updateOnboardingCatalogs } = useContext(
    CatalogsContext,
  ) as CatalogsContextType;

  const keys = useMemo(() => catalogKeys.join(), []);

  useQuery({
    queryKey: ['global-catalogs', keys],
    queryFn: async () => {
      const response = await getCatalogDetailsByCatalogCodes(keys);

      if (!response.success || !response.data?.data) {
        throw new Error(
          response.errorMessage || 'Error al obtener los catálogos',
        );
      }

      return response;
    },
    enabled: !onboardingCatalogs.loaded && !onboardingCatalogs.error,
    retry: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: (response) => {
      if (response.success && response.data && response.data.data) {
        const filteredCatalogs = buildCatalogs(response.data.data);
        updateOnboardingCatalogs({
          ...staticCatalogs,
          loaded: true,
          error: false,
          ...filteredCatalogs,
        });
      }
    },
    onError: () => {
      updateOnboardingCatalogs({
        ...onboardingCatalogs,
        error: true,
      });
    },
  });

  return onboardingCatalogs;
};

export default useCatalogs;
